import { JPY_CURRENCY_CODE } from 'constants/account'
import { humanizeLongNumberWithLocale } from './formatters'
import { getLanguageCode } from './mws'

export const parseCurrency = (value, currencyCode, options = {}) => {
  const newValue = value || '0'

  return parseFloat(newValue).toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
    ...options
  })
}

export const getCurrencyCode = (code = 'us') => {
  /* There are some cases when even with the default value set
      the code variable returns null so the next line is a safeguard */
  const countryCode = code || 'us'

  switch (countryCode.toLowerCase()) {
    case 'us':
      return 'USD'
    case 'ca':
      return 'CAD'
    case 'in':
      return 'INR'
    case 'mx':
      return 'MXN'
    case 'uk':
      return 'GBP'
    case 'jp':
      return 'JPY'
    case 'ae':
      return 'AED'
    case 'sa':
      return 'SAR'
    case 'pl':
      return 'PLN'
    case 'au':
      return 'AUD'
    case 'br':
      return 'BRL'
    case 'tr':
      return 'TRY'
    case 'eg':
      return 'EGP'
    case 'se':
      return 'SEK'
    case 'sg':
      return 'SGD'
    case 'cn':
      return 'CNY'
    case 'za':
      return 'ZAR'
    default:
      return 'EUR'
  }
}

export const getCountryCode = (currency = 'USD') => {
  const currencyCode = currency.toUpperCase()

  switch (currencyCode) {
    case 'USD':
      return 'us'
    case 'CAD':
      return 'ca'
    case 'INR':
      return 'in'
    case 'MXN':
      return 'mx'
    case 'GBP':
      return 'uk'
    case 'JPY':
      return 'jp'
    case 'AED':
      return 'ae'
    case 'SAR':
      return 'sa'
    case 'PLN':
      return 'pl'
    case 'AUD':
      return 'au'
    case 'BRL':
      return 'br'
    case 'TRY':
      return 'tr'
    case 'EGP':
      return 'eg'
    case 'SEK':
      return 'se'
    case 'SGD':
      return 'sg'
    case 'CNY':
      return 'cn'
    case 'ZAR':
      return 'za'
    default:
      return 'eur'
  }
}

export function getCurrencySymbolFromCode(code = 'us') {
  switch (code?.toLowerCase()) {
    case 'us':
    case 'ca':
    case 'mx':
    case 'au':
    case 'sg':
      return '$'
    case 'in':
      return '₹'
    case 'uk':
      return '£'
    case 'jp':
      return '¥'
    case 'ae':
      return 'AED '
    case 'sa':
      return 'SAR '
    case 'br':
      return 'R$'
    case 'tr':
      return '₺'
    case 'eg':
      return 'EGP '
    case 'se':
      return 'SEK '
    case 'cn':
      return 'CN¥ '
    case 'pl':
      return 'PLN '
    case 'za':
      return 'ZAR '
    default:
      return '€'
  }
}

export const formatCurrencyWithCountryCode = (value, code) => {
  const countryCode = code || 'us'
  return parseCurrency(value, getCurrencyCode(countryCode.toLowerCase()))
}

export const formatCurrencyValueWithSymbol = (
  val,
  code,
  prependMinus,
  decimalPlaces = 2
) => {
  let newVal = val
  if (newVal == null) {
    return ''
  }
  const formattedCode = code ? code.toLowerCase() : code
  let prefix = ''
  const currencySymbol = getCurrencySymbolFromCode(formattedCode)
  const languageCode = getLanguageCode(formattedCode)

  // when prependMinus is true and value is negative
  // prepend '-' sign and display absolute value
  if (newVal < 0 && prependMinus) {
    prefix = '-'
    newVal = Math.abs(newVal)
  }

  // if not rendering decimals we round down
  if (decimalPlaces === 0) {
    newVal = Math.floor(newVal)
  }

  const formattedAndRoundedValue = humanizeLongNumberWithLocale(
    newVal,
    languageCode,
    decimalPlaces,
    decimalPlaces
  )

  return `${prefix}${currencySymbol}${formattedAndRoundedValue}`
}

export const formatAccounting = number => {
  const num = number || 0.0

  if (num < 0) {
    return `($${Math.abs(num).toFixed(2)})`
  }

  return `$${parseFloat(num).toFixed(2)}`
}

export const currencyIsDecimalized = currencyCode => {
  return ![JPY_CURRENCY_CODE].includes(currencyCode?.toLowerCase())
}

/**
 * Helper function to convert currency amounts based on exchange rates.
 * Use the useCurrencyExchangeRates from hooks/currency to get the rates.
 */
export const convertCurrency = ({ value, rates, fromCurrency, toCurrency }) => {
  if (
    !value ||
    !rates ||
    !fromCurrency ||
    !toCurrency ||
    value === 0 ||
    fromCurrency === toCurrency
  ) {
    return value
  }

  const rateKey = `${fromCurrency}/${toCurrency}`
  // We only have USD rates, so if the fromCurrency is not USD,
  // we first convert it to USD, then to the target currency.
  if (!rateKey.includes('USD')) {
    const usdValue = convertCurrency({
      value,
      rates,
      fromCurrency,
      toCurrency: 'USD'
    })
    return convertCurrency({
      value: usdValue,
      rates,
      fromCurrency: 'USD',
      toCurrency
    })
  }

  const rate = rates[rateKey]

  if (!rate) {
    return value
  }

  return value * rate
}
